import { HttpBackend, HttpClient, HttpContext, HttpEvent, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EnvService } from './env.service';
import { Observable } from 'rxjs';
import { UrlHelper } from '../utils/url.helper';
import {
	buildStageCookieName,
	CSRF_TOKEN_COOKIE,
	X_CSRF_TOKEN_HEADER,
	X_CURRENT_URL_BASE64_HEADER,
	X_SUBDOMAIN_HEADER,
	getNewestCookieValue,
} from '@mira-avant/mtlibs';

type HttpOptions = {
	headers?: HttpHeaders | { [header: string]: string | string[] };
	context?: HttpContext;
	params?: HttpParams | { [param: string]: string | string[] };
	reportProgress?: boolean;
	withCredentials?: boolean;
	transferCache?: boolean | { includeHeaders?: string[] };
};

export enum ApiRoots {
	JOURNEY_API,
	JOURNEY_UPLOADER,
	COACH_API,
	COACH_AUDIO,
}

@Injectable({
	providedIn: 'root',
})
export class ApiService {
	private apiUrl: string;
	private uploaderApiUrl: string;
	private coachApiRoot: string;
	private coachAudioRoot: string;
	private csrfInitializationInFlight: Promise<void> | undefined;
	private rawHttp: HttpClient;

	constructor(
		private http: HttpClient,
		httpBackend: HttpBackend,
		private envService: EnvService
	) {
		this.rawHttp = new HttpClient(httpBackend);
		this.apiUrl = this.envService.getApiUrl();
		this.uploaderApiUrl = this.envService.getUploaderApiUrl();
		this.coachApiRoot = this.envService.getCoachApiRoot();
		this.coachAudioRoot = this.envService.getCoachAudioRoot();
		void this.initializeCsrfToken();
	}

	private async initializeCsrfToken(): Promise<void> {
		if (this.getCsrfToken()) {
			return;
		}

		if (!this.csrfInitializationInFlight) {
			this.csrfInitializationInFlight = new Promise<void>((resolve, reject) => {
				const url = this.buildUrl('cognito/csrf-token', ApiRoots.JOURNEY_API);
				// Use a raw HttpClient here to avoid interceptor-induced DI cycles during service construction.
				this.rawHttp
					.get(url, {
						headers: this.getAuthHeaders(),
						withCredentials: true,
					})
					.subscribe({
						next: () => {
							this.csrfInitializationInFlight = undefined;
							resolve();
						},
						error: error => {
							this.csrfInitializationInFlight = undefined;
							reject(error);
						},
					});
			});
		}

		try {
			await this.csrfInitializationInFlight;
		} catch (error) {
			console.warn('CSRF initialization failed during ApiService initialization', error);
		}
	}

	private getCsrfToken(): string | undefined {
		const cookieName = buildStageCookieName(CSRF_TOKEN_COOKIE, this.envService.getStage());

		return getNewestCookieValue(document.cookie, cookieName);
	}

	private getAuthHeaders(apiRoot: ApiRoots = ApiRoots.JOURNEY_API): HttpHeaders {
		const currentUrl = btoa(window.location.href);
		const subdomain: string = UrlHelper.getLeftMostSubDomains();
		const csrfToken = this.getCsrfToken();

		const headers: { [header: string]: string } = {
			'content-type': 'application/json',
			[X_CURRENT_URL_BASE64_HEADER]: currentUrl,
			[X_SUBDOMAIN_HEADER]: subdomain,
		};

		if (csrfToken) {
			headers[X_CSRF_TOKEN_HEADER] = csrfToken;
		}

		return new HttpHeaders(headers);
	}

	private buildUrl(endpoint: string, apiRoot: ApiRoots = ApiRoots.JOURNEY_API): string {
		endpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
		let baseUrl: string;

		switch (apiRoot) {
			case ApiRoots.JOURNEY_API:
				baseUrl = this.apiUrl;
				break;
			case ApiRoots.JOURNEY_UPLOADER:
				baseUrl = this.uploaderApiUrl;
				break;
			case ApiRoots.COACH_API:
				baseUrl = this.coachApiRoot;
				break;
			case ApiRoots.COACH_AUDIO:
				baseUrl = this.coachAudioRoot;
				break;
			default:
				baseUrl = this.apiUrl;
		}

		return `${baseUrl}/${endpoint}`;
	}

	private buildOptions(options: HttpOptions = {}, apiRoot: ApiRoots = ApiRoots.JOURNEY_API): HttpOptions {
		return {
			...options,
			headers: this.getAuthHeaders(apiRoot),
			withCredentials: true,
		};
	}

	public get<T>(
		endpoint: string,
		options: HttpOptions = {},
		apiRoot: ApiRoots = ApiRoots.JOURNEY_API
	): Observable<T> {
		return this.http.get<T>(this.buildUrl(endpoint, apiRoot), this.buildOptions(options, apiRoot));
	}

	/**
	 * Gets a blob response from the API (for file downloads)
	 * @param endpoint The API endpoint
	 * @param options HTTP options
	 * @param useUploaderUrl Whether to use the uploader API URL
	 * @returns Observable of Blob data
	 */
	public getBlob(
		endpoint: string,
		options: Omit<HttpOptions, 'responseType'> = {},
		apiRoot: ApiRoots = ApiRoots.JOURNEY_API
	): Observable<Blob> {
		const url = this.buildUrl(endpoint, apiRoot);
		const headers = this.getAuthHeaders(apiRoot);

		return this.http.get(url, {
			...options,
			headers,
			responseType: 'blob',
			withCredentials: true,
		});
	}

	public post<T>(
		endpoint: string,
		body: any,
		options: HttpOptions = {},
		apiRoot: ApiRoots = ApiRoots.JOURNEY_API
	): Observable<T> {
		return this.http.post<T>(this.buildUrl(endpoint, apiRoot), body, this.buildOptions(options, apiRoot));
	}

	public put<T>(
		endpoint: string,
		body: any,
		options: HttpOptions = {},
		apiRoot: ApiRoots = ApiRoots.JOURNEY_API
	): Observable<T> {
		return this.http.put<T>(this.buildUrl(endpoint, apiRoot), body, this.buildOptions(options, apiRoot));
	}

	public delete<T>(
		endpoint: string,
		options: HttpOptions = {},
		apiRoot: ApiRoots = ApiRoots.JOURNEY_API
	): Observable<T> {
		return this.http.delete<T>(this.buildUrl(endpoint, apiRoot), this.buildOptions(options, apiRoot));
	}

	public patch<T>(
		endpoint: string,
		body: any,
		options: HttpOptions = {},
		apiRoot: ApiRoots = ApiRoots.JOURNEY_API
	): Observable<T> {
		return this.http.patch<T>(this.buildUrl(endpoint, apiRoot), body, this.buildOptions(options, apiRoot));
	}

	public upload<T>(
		endpoint: string,
		formData: FormData,
		options: {
			reportProgress?: boolean;
			observe: 'events';
		},
		apiRoot: ApiRoots = ApiRoots.JOURNEY_UPLOADER
	): Observable<HttpEvent<T>> {
		const url = this.buildUrl(endpoint, apiRoot);
		const subdomain: string = UrlHelper.getLeftMostSubDomains();
		const csrfToken = this.getCsrfToken();

		const headerValues: { [header: string]: string } = {
			[X_SUBDOMAIN_HEADER]: subdomain,
		};

		if (csrfToken) {
			headerValues[X_CSRF_TOKEN_HEADER] = csrfToken;
		}

		const headers = new HttpHeaders(headerValues);

		return this.http.post<T>(url, formData, {
			headers,
			reportProgress: true,
			observe: 'events',
			withCredentials: true,
		});
	}
}
